import { wrapInList, splitListItem, sinkListItem, liftListItem } from 'prosemirror-schema-list';
import { stateful, useEffect, useState } from '@adornis/functional-lit/stateful.js';
import { html } from 'lit';
import { ref } from 'lit/directives/ref.js';
import { baseKeymap, toggleMark } from 'prosemirror-commands';
import { history, undo, redo } from 'prosemirror-history';
import { keymap } from 'prosemirror-keymap';
import { EditorState, Selection, type Command } from 'prosemirror-state';
import { EditorView } from 'prosemirror-view';
import { commentBodyFromText, commentPlainText, commentSchema, normalizeCommentBody, safeCommentHref, COMMENT_TEXT_LIMIT } from '../../comments.js';
import type { WebsiteBuilderNodeJSON } from '../../types.js';

export interface CommentEditorProps {
  id: string;
  body?: WebsiteBuilderNodeJSON;
  text?: string;
  onSubmit: (body: WebsiteBuilderNodeJSON) => Promise<void> | void;
  onCancel: () => void;
  label?: string;
}

export const CommentEditor = stateful((props: CommentEditorProps) => {
  const [holder] = useState<{ element?: HTMLElement; view?: EditorView }>({}, [props.id]);
  const [pending, setPending] = useState(false, [props.id]);
  const [error, setError] = useState('', [props.id]);
  const [count, setCount] = useState(props.text?.length ?? 0, [props.id]);
  const [linkOpen, setLinkOpen] = useState(false, [props.id]);
  const [link, setLink] = useState('', [props.id]);
  const [current] = useState({ props, pending: false, submit: () => {} }, [props.id]);
  current.props = props;
  current.pending = pending;
  const submit = async () => {
    if (!holder.view || current.pending) return;
    const body = normalizeCommentBody(holder.view.state.doc.toJSON());
    if (!body) { setError(`Bitte einen Kommentar mit höchstens ${COMMENT_TEXT_LIMIT} Zeichen schreiben.`); return; }
    current.pending = true;
    setPending(true); setError('');
    try { await current.props.onSubmit(body); }
    catch (failure) { setError(failure instanceof Error ? failure.message : 'Kommentar konnte nicht gespeichert werden.'); }
    finally { current.pending = false; setPending(false); }
  };
  current.submit = () => void submit();
  useEffect(() => {
    if (!holder.element) return;
    const body = normalizeCommentBody(props.body, true) ?? commentBodyFromText(props.text ?? '');
    holder.view = new EditorView(holder.element, {
      state: EditorState.create({ schema: commentSchema, doc: commentSchema.nodeFromJSON(body), plugins: [history(), keymap({
        'Mod-End': (state, dispatch) => { dispatch?.(state.tr.setSelection(Selection.atEnd(state.doc))); return true; },
        'Mod-Home': (state, dispatch) => { dispatch?.(state.tr.setSelection(Selection.atStart(state.doc))); return true; },
        Enter: splitListItem(commentSchema.nodes.list_item!),
        Tab: sinkListItem(commentSchema.nodes.list_item!), 'Shift-Tab': liftListItem(commentSchema.nodes.list_item!),
        'Mod-z': undo, 'Mod-y': redo, 'Mod-Shift-z': redo,
        'Mod-b': toggleMark(commentSchema.marks.bold!), 'Mod-i': toggleMark(commentSchema.marks.italic!),
        'Mod-Enter': () => { current.submit(); return true; },
        Escape: () => { current.props.onCancel(); return true; },
      }), keymap(baseKeymap)] }),
      attributes: { role: 'textbox', 'aria-multiline': 'true', 'aria-label': props.label ?? 'Kommentar schreiben', class: 'we-comment-input' },
      dispatchTransaction: tr => {
        const view = holder.view;
        if (!view) return;
        view.updateState(view.state.apply(tr));
        setCount(commentPlainText(view.state.doc).length);
      },
    });
    setCount(commentPlainText(holder.view.state.doc).length);
    holder.view.focus();
    return () => { holder.view?.destroy(); holder.view = undefined; };
  }, [props.id]);
  const run = (command: Command) => {
    const view = holder.view;
    if (view) { command(view.state, tr => view.dispatch(tr), view); view.focus(); }
  };
  return html`<section class="we-comment-editor" aria-busy=${String(pending)}>
    <div role="toolbar" aria-label="Kommentar formatieren">
      <button type="button" ?disabled=${pending} @mousedown=${(event: MouseEvent) => event.preventDefault()} @click=${() => run(toggleMark(commentSchema.marks.bold!))}>Fett</button>
      <button type="button" ?disabled=${pending} @mousedown=${(event: MouseEvent) => event.preventDefault()} @click=${() => run(toggleMark(commentSchema.marks.italic!))}>Kursiv</button>
      <button type="button" ?disabled=${pending} @mousedown=${(event: MouseEvent) => event.preventDefault()} @click=${() => run(wrapInList(commentSchema.nodes.bullet_list!))}>Liste</button>
      <button type="button" ?disabled=${pending} @click=${() => setLinkOpen(!linkOpen)}>Link</button>
    </div>
    ${linkOpen ? html`<label>Linkziel<input type="url" .value=${link} @input=${(event: Event) => setLink((event.target as HTMLInputElement).value)} /></label><button type="button" @click=${() => {
      const href = safeCommentHref(link); if (!href) { setError('Bitte einen gültigen Link eingeben.'); return; }
      run(toggleMark(commentSchema.marks.link!, { href })); setLinkOpen(false); setError('');
    }}>Link übernehmen</button>` : ''}
    <div ${ref(element => { holder.element = element as HTMLElement; })}></div>
    <small aria-live="polite">${count} / ${COMMENT_TEXT_LIMIT}</small>
    ${error ? html`<p role="alert">${error}</p>` : ''}
    <div><button type="button" ?disabled=${pending} @click=${props.onCancel}>Abbrechen</button>
    <button type="button" ?disabled=${pending || count > COMMENT_TEXT_LIMIT} @click=${submit}>${pending ? 'Speichert …' : 'Kommentar speichern'}</button></div>
  </section>`;
});
